import SLBLogo from '../../../images/slb-logo-png.png';
import { formatDate } from '../../../utilities/dateHelpers';

interface Widget {
  title: string;
  selector: string;
}

export const handleGenerateReport = async (setIsGeneratingReport: (loading: boolean) => void): Promise<void> => {
  setIsGeneratingReport(true);
  try {
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const html2canvas = (await import('html2canvas')).default;
    const jsPDF = (await import('jspdf')).default;

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let isFirstPage = true;

    const addPageHeader = (title: string) => {
      pdf.setFillColor('#01485e');
      pdf.rect(0, 0, pageWidth, 50, 'F');
      pdf.addImage(SLBLogo, 'PNG', 30, 15, 44, 24);
      pdf.setFontSize(12);
      pdf.setTextColor(255, 255, 255);
      pdf.text(`OptiLift`, pageWidth - 70, 30, { align: 'right' });
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text(title, pageWidth / 2, 80, { align: 'center' });
    };

    // Get widgets dynamically
    const widgets: Widget[] = [];

    if (document.querySelector('.real-life-summary-container')) {
      widgets.push({ title: 'Summary', selector: '.real-life-summary-container' });
    }

    const widgetMap: Record<string, string> = {
      'manufacturer-installations': 'Manufacturer installations',
      'manufacturer-runlife': 'Manufacturer runlife',
      'overall-statistics': 'Overall statistics',
      'field-installations': 'Field installations',
      'manufacturer-statistics': 'Manufacturer statistics',
      'field-statistics': 'Field statistics',
      'formation-statistics': 'Formation statistics',
      'average-runlife-by-field': 'Average runlife by field',
      'runlife-stats-by-installnumber': 'Runlife stats by install number',
    };

    document.querySelectorAll('[id^="grid-item-"]').forEach((item) => {
      const itemId = item.id.replace('grid-item-', '');
      const title =
        widgetMap[itemId] ||
        itemId
          .split('-')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
      widgets.push({ title, selector: `#${item.id}` });
    });

    if (widgets.length === 0) {
      addPageHeader('ESP Runlife Dashboard - No Data');
      pdf.setFontSize(14);
      pdf.setTextColor(255, 0, 0);
      pdf.text('No dashboard widgets found to export.', pageWidth / 2, pageHeight / 2, { align: 'center' });
      pdf.save(`ESP_Runlife_Dashboard_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      return;
    }

    // Process each widget
    for (const widget of widgets) {
      const element = document.querySelector(widget.selector) as HTMLElement;

      if (!element) {
        if (!isFirstPage) pdf.addPage();
        isFirstPage = false;
        addPageHeader(widget.title);
        pdf.setFontSize(12);
        pdf.setTextColor(255, 0, 0);
        pdf.text(`Widget not found: ${widget.selector}`, pageWidth / 2, pageHeight / 2, { align: 'center' });
        continue;
      }

      if (!isFirstPage) pdf.addPage();
      isFirstPage = false;
      addPageHeader(widget.title);

      try {
        // table data
        const hasTable = element.querySelector('table, .data-table, .table-responsive');

        if (hasTable) {
          const tableElement = hasTable as HTMLElement;
          const tableData: any[] = [];
          let tableHeaders: string[] = [];

          const headerCells = tableElement.querySelectorAll('th, .table-header, [class*="header-cell"]');
          tableHeaders = Array.from(headerCells).map((cell) => (cell as HTMLElement).textContent?.trim() || '');

          if (tableHeaders.length === 0) {
            const firstRow = tableElement.querySelector('tr, .table-row, [class*="row"]:first-child');
            if (firstRow) {
              const firstRowCells = firstRow.querySelectorAll('td, .table-cell, [class*="cell"]');
              tableHeaders = Array.from(firstRowCells).map((cell) => (cell as HTMLElement).textContent?.trim() || '');
            }
          }

          // Extract data rows
          const dataRows = tableElement.querySelectorAll('tbody tr, .table-body .table-row, [class*="data-row"]');
          if (dataRows.length === 0) {
            const allRows = tableElement.querySelectorAll('tr, .table-row, [class*="row"]');
            for (let i = 1; i < allRows.length; i++) {
              const row = allRows[i];
              const cells = row.querySelectorAll('td, .table-cell, [class*="cell"]');
              const rowData = Array.from(cells).map((cell) => (cell as HTMLElement).textContent?.trim() || '');
              if (rowData.some((cell) => cell)) tableData.push(rowData);
            }
          } else {
            dataRows.forEach((row) => {
              const cells = row.querySelectorAll('td, .table-cell, [class*="cell"]');
              const rowData = Array.from(cells).map((cell) => (cell as HTMLElement).textContent?.trim() || '');
              if (rowData.some((cell) => cell)) tableData.push(rowData);
            });
          }

          if (tableData.length > 0) {
            // Render table in PDF
            const startY = 100;
            let currentY = startY;
            const cellPadding = 10;
            const baseRowHeight = 30;
            const tableWidth = pageWidth - 60;

            // Calculate column widths
            const calculateColumnWidths = () => {
              pdf.setFontSize(10);
              const minWidths = tableHeaders.map((header, colIndex) => {
                const headerWords = header.split(' ');
                const longestWord = headerWords.reduce(
                  (longest, word) => (pdf.getTextWidth(word) > pdf.getTextWidth(longest) ? word : longest),
                  '',
                );
                if (colIndex === 0) {
                  let maxWidth = Math.max(pdf.getTextWidth(longestWord) + cellPadding * 2, 150);

                  tableData.forEach((row) => {
                    if (row[colIndex]) {
                      const cellWidth = pdf.getTextWidth(String(row[colIndex])) + cellPadding * 2;
                      maxWidth = Math.max(maxWidth, cellWidth);
                    }
                  });
                  return Math.min(maxWidth, tableWidth * 0.3);
                }

                let maxWidth = Math.max(pdf.getTextWidth(longestWord) + cellPadding * 2, 80);

                tableData.forEach((row) => {
                  if (row[colIndex]) {
                    const cellWidth = pdf.getTextWidth(String(row[colIndex])) + cellPadding * 2;
                    maxWidth = Math.max(maxWidth, cellWidth);
                  }
                });

                return Math.min(maxWidth, tableWidth * 0.2);
              });

              const totalMinWidth = minWidths.reduce((sum, width) => sum + width, 0);
              if (totalMinWidth > tableWidth) {
                const scale = tableWidth / totalMinWidth;
                return minWidths.map((width) => width * scale);
              }

              const extraSpace = tableWidth - totalMinWidth;
              const totalWeight = minWidths.reduce((sum, width) => sum + width, 0);
              return minWidths.map((width) => {
                const proportion = width / totalWeight;
                return width + extraSpace * proportion;
              });
            };

            const columnWidths = calculateColumnWidths();

            // Helper function to wrap text
            const wrapText = (text: string, maxWidth: number, fontSize = 10): string[] => {
              pdf.setFontSize(fontSize);
              const words = text.split(' ');
              const lines: string[] = [];
              let currentLine = '';

              for (const word of words) {
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                const testWidth = pdf.getTextWidth(testLine);

                if (testWidth <= maxWidth) {
                  currentLine = testLine;
                } else {
                  if (currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                  } else {
                    let part = '';
                    for (let i = 0; i < word.length; i++) {
                      const nextPart = part + word[i];
                      if (pdf.getTextWidth(nextPart) > maxWidth) {
                        if (part) lines.push(part);
                        part = word[i];
                      } else {
                        part = nextPart;
                      }
                    }
                    if (part) lines.push(part);
                  }
                }
              }

              if (currentLine) {
                lines.push(currentLine);
              }

              return lines;
            };

            const headerLines = tableHeaders.map((header, index) => {
              const maxTextWidth = columnWidths[index] - cellPadding * 2;
              return wrapText(header, maxTextWidth);
            });

            const maxHeaderLines = Math.max(...headerLines.map((lines) => lines.length));
            const headerHeight = Math.max(baseRowHeight, maxHeaderLines * 12 + cellPadding);

            pdf.setFillColor(30, 58, 88);
            pdf.rect(30, currentY, tableWidth, headerHeight, 'F');
            pdf.setFontSize(10);
            pdf.setTextColor(255, 255, 255);
            pdf.setFont('helvetica', 'bold');

            tableHeaders.forEach((_, index) => {
              const startX = 30 + columnWidths.slice(0, index).reduce((sum, w) => sum + w, 0) + cellPadding;
              const lines = headerLines[index];

              // Center the wrapped text vertically
              const totalTextHeight = lines.length * 12;
              const startY = currentY + (headerHeight - totalTextHeight) / 2 + 12;

              lines.forEach((line, lineIndex) => {
                const y = startY + lineIndex * 12;
                pdf.text(line, startX, y);
              });
            });

            currentY += headerHeight;

            // Draw data rows
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(0, 0, 0);

            tableData.forEach((rowData, rowIndex) => {
              if (rowIndex % 2 === 0) {
                pdf.setFillColor(248, 249, 250);
              } else {
                pdf.setFillColor(255, 255, 255);
              }
              pdf.rect(30, currentY, tableWidth, baseRowHeight, 'F');

              rowData.forEach((cellData: string, colIndex: number) => {
                if (colIndex < tableHeaders.length) {
                  const colWidth = columnWidths[colIndex];
                  const x = 30 + columnWidths.slice(0, colIndex).reduce((sum, w) => sum + w, 0) + cellPadding;
                  const y = currentY + baseRowHeight / 2 + 3;
                  const maxWidth = colWidth - cellPadding * 2;
                  const cellLines = wrapText(String(cellData), maxWidth);
                  cellLines.forEach((line, lineIdx) => {
                    const lineY = y + lineIdx * 12 - (cellLines.length - 1) * 6;
                    pdf.text(line, x, lineY);
                  });
                }
              });

              currentY += baseRowHeight;
              if (currentY > pageHeight - 100) {
                pdf.addPage();
                addPageHeader(widget.title + ' (continued)');
                currentY = 120;
              }
            });

            // Draw table border
            pdf.setDrawColor(100, 100, 100);
            pdf.setLineWidth(1);
            pdf.rect(30, startY, tableWidth, currentY - startY, 'S');
          }
        } else {
          // Summary widget
          if (widget.title === 'Summary') {
            const summaryTiles = element.querySelectorAll(
              '[class*="summary-count"], .summary-count, [data-testid*="summary"]',
            );

            if (summaryTiles.length > 0) {
              const tempContainer = document.createElement('div');
              tempContainer.style.cssText = `
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 15px;
                padding: 20px;
                background: transparent;
                max-width: 600px;
                justify-items: center;
              `;

              summaryTiles.forEach((tile) => {
                const clonedTile = tile.cloneNode(true) as HTMLElement;
                clonedTile.style.cssText += '; width: 160px; height: auto; margin: 0; box-sizing: border-box;';
                tempContainer.appendChild(clonedTile);
              });

              document.body.appendChild(tempContainer);

              try {
                const canvas = await html2canvas(tempContainer, {
                  backgroundColor: null,
                  scale: 1.5,
                  useCORS: true,
                  allowTaint: true,
                  foreignObjectRendering: false,
                  imageTimeout: 15000,
                  removeContainer: false,
                  logging: false,
                });

                const imgData = canvas.toDataURL('image/jpeg', 0.75);
                const maxWidth = pageWidth - 60;
                const maxHeight = pageHeight - 180;
                const imgAspectRatio = canvas.width / canvas.height;

                let imgWidth = maxWidth;
                let imgHeight = imgWidth / imgAspectRatio;

                if (imgHeight > maxHeight) {
                  imgHeight = maxHeight;
                  imgWidth = imgHeight * imgAspectRatio;
                }

                const x = (pageWidth - imgWidth) / 2;
                const y = 120;
                pdf.addImage(imgData, 'JPEG', x, y, imgWidth, imgHeight);
              } finally {
                document.body.removeChild(tempContainer);
              }
            } else {
              const canvas = await html2canvas(element, {
                backgroundColor: null,
                scale: 1.5,
                useCORS: true,
                allowTaint: true,
                foreignObjectRendering: false,
                imageTimeout: 15000,
                removeContainer: false,
                logging: false,
                width: element.offsetWidth,
                height: element.offsetHeight,
                windowWidth: element.offsetWidth,
                windowHeight: element.offsetHeight,
              });

              const imgData = canvas.toDataURL('image/jpeg', 0.75);
              const maxWidth = pageWidth - 60;
              const maxHeight = pageHeight - 180;
              const imgAspectRatio = canvas.width / canvas.height;

              let imgWidth = maxWidth;
              let imgHeight = imgWidth / imgAspectRatio;

              if (imgHeight > maxHeight) {
                imgHeight = maxHeight;
                imgWidth = imgHeight * imgAspectRatio;
              }

              const x = (pageWidth - imgWidth) / 2;
              const y = 120;
              pdf.addImage(imgData, 'JPEG', x, y, imgWidth, imgHeight);
            }
          } else {
            let chartElement: HTMLElement = element;
            if (widget.selector === '#grid-item-runlife-stats-by-installnumber') {
              const statsContent = document.getElementById('runlife-stats-chart-content');
              if (statsContent) {
                chartElement = statsContent;
              }
            } else {
              const parentChartContainer = element.querySelector('.runlife-bar-chart-content');
              if (parentChartContainer && parentChartContainer instanceof HTMLElement) {
                chartElement = parentChartContainer;
              }
            }

            await new Promise((resolve) => setTimeout(resolve, 500));

            const canvas = await html2canvas(chartElement, {
              backgroundColor: '#131f2e',
              scale: 1.5,
              useCORS: true,
              allowTaint: true,
              foreignObjectRendering: false,
              imageTimeout: 15000,
              removeContainer: false,
              logging: false,
              width: chartElement.offsetWidth,
              height: chartElement.offsetHeight,
              windowWidth: chartElement.offsetWidth,
              windowHeight: chartElement.offsetHeight,
            });

            const pxPerPt = 96 / 72;
            const maxPdfImgPx = Math.floor((pageWidth - 60) * pxPerPt);

            let finalCanvas: HTMLCanvasElement = canvas;
            if (canvas.width > maxPdfImgPx) {
              const scale = maxPdfImgPx / canvas.width;
              const scaledCanvas = document.createElement('canvas');
              scaledCanvas.width = Math.max(1, Math.floor(canvas.width * scale));
              scaledCanvas.height = Math.max(1, Math.floor(canvas.height * scale));
              const ctx = scaledCanvas.getContext('2d');
              if (ctx) ctx.drawImage(canvas, 0, 0, scaledCanvas.width, scaledCanvas.height);
              finalCanvas = scaledCanvas;
            }

            const imgData = finalCanvas.toDataURL('image/jpeg', 0.7);
            const maxWidth = pageWidth - 60;
            const maxHeight = pageHeight - 180;
            const imgAspectRatio = finalCanvas.width / finalCanvas.height;

            let imgWidth = maxWidth;
            let imgHeight = imgWidth / imgAspectRatio;

            if (imgHeight > maxHeight) {
              imgHeight = maxHeight;
              imgWidth = imgHeight * imgAspectRatio;
            }

            const x = (pageWidth - imgWidth) / 2;
            const y = 120;
            pdf.addImage(imgData, 'JPEG', x, y, imgWidth, imgHeight);
          }
        }
      } catch (error) {
        console.error(`Error capturing widget ${widget.title}:`, error);
        pdf.setFontSize(12);
        pdf.setTextColor(255, 0, 0);
        pdf.text(`Error capturing ${widget.title}`, pageWidth / 2, pageHeight / 2, { align: 'center' });
      }
    }

    // Add footer
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    const footerText = formatDate(new Date(), true);
    pdf.text(`Generated By: Auto Export`, 30, pageHeight - 30);
    pdf.text(`Generated on: ${footerText}`, pageWidth - 30, pageHeight - 30, { align: 'right' });

    // Save PDF
    pdf.save(`ESP_Runlife_Dashboard_Report_${new Date().toISOString().split('T')[0]}.pdf`);
  } catch (error) {
    console.error('Error generating PDF report:', error);
  } finally {
    setIsGeneratingReport(false);
  }
};
